import { forwardRef, useEffect, useMemo } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import {
  Flex,
  Icon,
  registerIcon,
  Button,
  Text,
  useI18n,
  defaultThemeProp,
  useLiveLog,
  ExpandCollapse,
  Alert,
  useDirection,
  EmptyState,
  Progress,
  hasProp,
  ErrorState
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent, OmitStrict } from '@pega/cosmos-react-core';
import * as timesIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/times.icon';
import * as caretLeftIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-left.icon';
import * as caretRightIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-right.icon';
import { StyledAlert } from '@pega/cosmos-react-core/lib/components/Badges/Alert';
import { RichTextViewer } from '@pega/cosmos-react-rte';

import type { ChatComposerProps } from './ChatComposer';

registerIcon(timesIcon, caretLeftIcon, caretRightIcon);

export const StyledSuggestedReplyHeader = styled.div``;
export const StyledSuggestedReplyContent = styled.div``;
export const StyledReply = styled.div``;
export const StyledRepliesAction = styled.div``;
export const StyledNotification = styled.div``;
const maxNumOfLines = 5;
const minNumOfLines = 2;

export const StyledSuggestedReplyIcon = styled(Icon)<{ suggestedReplyCollapsed?: boolean }>(({
  suggestedReplyCollapsed,
  theme
}) => {
  const { rtl } = useDirection();
  const {
    base: {
      animation: {
        speed,
        timing: { ease }
      }
    }
  } = theme;
  return css`
    transition-property: transform;
    transition-duration: ${speed};
    transition-timing-function: ${ease};
    ${suggestedReplyCollapsed &&
    css`
      transform: rotateZ(${rtl ? '0deg' : '-90deg'});
    `}
  `;
});

StyledSuggestedReplyIcon.defaultProps = defaultThemeProp;

const StyledSuggestedReplyContainer = styled.div(props => {
  const { theme } = props;
  return css`
    border-top: 0.0625rem solid ${theme.base.palette['border-line']};
    background-color: ${theme.base.palette['primary-background']};

    > ${StyledSuggestedReplyHeader} {
      padding: ${theme.base.spacing};
      border-bottom: 0.0625rem solid ${theme.base.palette['border-line']};
    }

    ${StyledAlert} {
      position: relative;
      inset-block-start: calc(-0.25 * ${theme.base.spacing});
      inset-inline-start: calc(-0.5 * ${theme.base.spacing});
    }

    ${StyledSuggestedReplyContent} {
      padding: calc(2 * ${theme.base.spacing});
      position: relative;

      > ${StyledReply} {
        overflow-y: auto;
        max-height: calc(2.5 * ${theme.base.spacing} * ${maxNumOfLines});
        min-height: calc(2.5 * ${theme.base.spacing} * ${minNumOfLines});
      }
    }
  `;
});

StyledSuggestedReplyContainer.defaultProps = defaultThemeProp;

export interface SuggestedReplyItem {
  /** Id for this suggested reply */
  id: string;
  /** Suggested reply message */
  message: string;
  /** type of this suggested reply */
  type: 'dialog' | 'genAIReply';
}

interface ReplyLoadingState extends OmitStrict<SuggestedReplyItem, 'message'> {
  /** loading until reply is retrieved */
  loading: boolean;
}

interface ReplyErrorState extends SuggestedReplyItem {
  /** If there are no suggestions from GenAI or if there is an error */
  errorType: 'noSuggestions' | 'error';
}

export type SuggestedReply = SuggestedReplyItem | ReplyLoadingState | ReplyErrorState;

export const isReplyLoadingState = (reply: SuggestedReply): reply is ReplyLoadingState => {
  return hasProp(reply, 'loading');
};

export const isReplyErrorState = (reply: SuggestedReply): reply is ReplyErrorState => {
  return hasProp(reply, 'errorType');
};

export interface SuggestedReplyPickerProps
  extends Pick<ChatComposerProps, 'onSend' | 'maxLength' | 'disabled'> {
  /** ref to the element */
  ref?: Ref<HTMLDivElement>;
  /** Suggested replies */
  replies?: SuggestedReply[];
  /** Id of the current reply */
  currentReplyId?: SuggestedReply['id'];
  /** Fired when user clicks on left or right buttons
   *  @param id Id of the new reply
   */
  onReplyChange: (id: SuggestedReply['id']) => void;
  /** Current state of the panel */
  collapsed: boolean;
  /** Callback for expand or collapse click */
  onExpandCollapse: () => void;
  /** Callback for the event when a suggested reply is selected */
  onSelect: (suggestedReply: SuggestedReply) => void;
  /** Show notification on picker header */
  showNotification?: boolean;
}

const StyledReplyType = styled(Flex)(({ theme, type }) => {
  return css`
    color: ${type === 'dialog' ? 'inherit' : theme.base.palette.ai};
  `;
});

StyledReplyType.defaultProps = defaultThemeProp;

const SuggestedReplyPicker: ForwardRefForwardPropsComponent<SuggestedReplyPickerProps> = forwardRef(
  function SuggestedReplyPicker(
    {
      replies = [],
      collapsed,
      onExpandCollapse,
      showNotification,
      onSelect,
      currentReplyId,
      onReplyChange,
      onSend,
      maxLength = 256,
      disabled = false,
      ...restProps
    }: PropsWithoutRef<SuggestedReplyPickerProps>,
    ref: SuggestedReplyPickerProps['ref']
  ) {
    const { announcePolite } = useLiveLog();
    const t = useI18n();
    const { rtl, ltr } = useDirection();

    const currentReply = replies.find(reply => reply.id === currentReplyId);
    const currentReplyIndex = replies.findIndex(reply => reply.id === currentReplyId);

    // Allows suggested replies to cycle
    const onSuggestedReplyChange = (mode: 'prev' | 'next') => {
      let newReplyIndex = mode === 'prev' ? currentReplyIndex - 1 : currentReplyIndex + 1;
      newReplyIndex = (newReplyIndex + replies.length) % replies.length;
      onReplyChange(replies[newReplyIndex].id);
    };

    // SR announcement when current reply changes
    useEffect(() => {
      if (!replies || replies.length === 0 || !currentReplyId) {
        return;
      }
      if (currentReply && !isReplyLoadingState(currentReply))
        announcePolite({
          message: `${t('suggested_replies_current_of_total', [
            currentReplyIndex + 1,
            replies.length
          ])},${currentReply?.message},${
            currentReply?.type === 'dialog' ? t('dialog') : t('pega_genAI')
          }`,
          type: 'acknowledgement'
        });
    }, [currentReply]);

    const onEditAnnouncement = () => {
      announcePolite({
        message: t('suggested_reply_copied_to_input_field'),
        type: 'acknowledgement'
      });
    };

    const suggestionType = useMemo(() => {
      if (currentReply && !collapsed) {
        let iconName = '';
        let label;
        if (currentReply.type === 'dialog') {
          iconName = 'script';
          label = t('dialog');
        } else {
          iconName = 'polaris-solid';
          label = t('pega_genAI');
        }

        return (
          <StyledReplyType
            container={{ alignItems: 'center', pad: 1, gap: 0.5 }}
            type={currentReply.type}
          >
            <Icon name={iconName} size='s' />
            {label}
          </StyledReplyType>
        );
      }

      return null;
    }, [currentReply, collapsed]);

    const suggestion = useMemo(() => {
      if (currentReply) {
        if (isReplyLoadingState(currentReply)) {
          return (
            <Progress
              placement='block'
              message={t('loading')}
              liveConfig={{ contextualLabel: `${t('pega_genAI')} ${t('suggestion')}` }}
            />
          );
        }

        if (isReplyErrorState(currentReply)) {
          return (
            <Flex container={{ justify: 'center' }}>
              {currentReply.errorType === 'noSuggestions' ? (
                <EmptyState message={currentReply.message} />
              ) : (
                <ErrorState message={currentReply.message} />
              )}
            </Flex>
          );
        }

        return (
          <Flex container as={StyledReply}>
            <RichTextViewer type='markdown' content={currentReply.message} />
          </Flex>
        );
      }

      return null;
    }, [currentReply]);

    return (
      <StyledSuggestedReplyContainer ref={ref} {...restProps}>
        <Flex
          as={StyledSuggestedReplyHeader}
          container={{
            justify: 'between',
            alignContent: 'center'
          }}
        >
          <Flex
            container={{
              alignItems: 'center',
              gap: 1
            }}
          >
            {ltr && (
              <Button
                variant='simple'
                label={collapsed ? t('expand') : t('collapse')}
                aria-label={t(
                  collapsed ? 'expand_suggested_replies' : 'collapse_suggested_replies'
                )}
                icon
                onClick={onExpandCollapse}
              >
                <StyledSuggestedReplyIcon name='caret-down' suggestedReplyCollapsed={collapsed} />
              </Button>
            )}

            <Flex container={{ gap: 0.5 }}>
              <Text variant='h3'>{t('suggested_replies')}</Text>
              {collapsed && showNotification && <Alert variant='urgent' />}
            </Flex>

            {!collapsed && replies?.length > 0 && (
              <Text variant='secondary'>{`- ${currentReplyIndex + 1}/${replies.length}`}</Text>
            )}

            {rtl && (
              <Button
                variant='simple'
                label={collapsed ? t('expand') : t('collapse')}
                aria-label={t(
                  collapsed ? 'expand_suggested_replies' : 'collapse_suggested_replies'
                )}
                icon
                onClick={onExpandCollapse}
              >
                <StyledSuggestedReplyIcon name='caret-down' suggestedReplyCollapsed={collapsed} />
              </Button>
            )}
          </Flex>
          {suggestionType}
        </Flex>
        <ExpandCollapse dimension='height' collapsed={collapsed} nullWhenCollapsed>
          <Flex
            container={{
              direction: 'column',
              gap: 1
            }}
            as={StyledSuggestedReplyContent}
          >
            {currentReply && replies.length ? (
              suggestion
            ) : (
              <EmptyState message={t('no_suggestion')} />
            )}
            <Flex
              container={{
                justify: 'center'
              }}
              as={StyledRepliesAction}
            >
              {replies.length > 1 && (
                <Button
                  icon
                  aria-label={t('prev_suggested_reply')}
                  variant='simple'
                  disabled={currentReplyId === undefined || replies.length <= 1}
                  onClick={() => {
                    onSuggestedReplyChange('prev');
                  }}
                >
                  <Icon name='caret-left' />
                </Button>
              )}
              <Button
                disabled={
                  disabled ||
                  currentReplyId === undefined ||
                  !replies ||
                  replies.length === 0 ||
                  (currentReply && isReplyLoadingState(currentReply) && currentReply.loading) ||
                  (currentReply && isReplyErrorState(currentReply) && !!currentReply.errorType)
                }
                onClick={() => {
                  if (
                    currentReply &&
                    !isReplyErrorState(currentReply) &&
                    !isReplyLoadingState(currentReply)
                  ) {
                    onSelect(currentReply);
                    onExpandCollapse();
                    onEditAnnouncement();
                  }
                }}
                aria-label={
                  replies.length > 0 && currentReplyId
                    ? t('edit_current_template', [
                        replies.length > 0 && currentReplyId
                          ? replies.findIndex(reply => reply.id === currentReplyId) + 1
                          : 0,
                        replies.length
                      ])
                    : undefined
                }
              >
                {t('edit')}
              </Button>
              <Button
                disabled={
                  disabled ||
                  currentReplyId === undefined ||
                  !replies ||
                  replies.length === 0 ||
                  (!!maxLength && replies.length > maxLength) ||
                  (currentReply && isReplyLoadingState(currentReply) && currentReply.loading) ||
                  (currentReply && isReplyErrorState(currentReply) && !!currentReply.errorType)
                }
                onClick={() => {
                  if (
                    currentReply &&
                    !isReplyErrorState(currentReply) &&
                    !isReplyLoadingState(currentReply)
                  ) {
                    onSend(currentReply.message);
                    onExpandCollapse();
                  }
                }}
                aria-label={
                  replies.length > 0 && currentReplyId
                    ? t('send_current_template', [
                        replies.length > 0 && currentReplyId
                          ? replies.findIndex(reply => reply.id === currentReplyId) + 1
                          : 0,
                        replies.length
                      ])
                    : undefined
                }
              >
                {t('send')}
              </Button>
              {replies.length > 1 && (
                <Button
                  icon
                  aria-label={t('next_suggested_reply')}
                  variant='simple'
                  onClick={() => {
                    onSuggestedReplyChange('next');
                  }}
                  disabled={currentReplyId === undefined || replies.length <= 1}
                >
                  <Icon name='caret-right' />
                </Button>
              )}
            </Flex>
          </Flex>
        </ExpandCollapse>
      </StyledSuggestedReplyContainer>
    );
  }
);

export default SuggestedReplyPicker;
